import { graphql } from 'gatsby';
import React, { useMemo } from 'react';
import { processImageAsset, processMandatoryImage } from 'helpers/csTypeProcessors';
import { nonFatalBuildError } from 'helpers/errorReporting';
import { CsAsset } from 'types/contentStack';
import { StyledTestimonialWithImage } from './styles';

const isPreview = process.env.GATSBY_ACYIVE_ENV === 'preview';

type TestimonialWithImageBlockProps = {
  testimonial_with_image: {
    testimonials: {
      image: CsAsset;
      quote: string;
      author_name: string | null;
      author_job_description: string | null;
    }[];
    previousButtonScreenReaderText?: string;
    nextButtonScreenReaderText?: string;
  };
};

export const query = graphql`
  fragment TestimonialWithImageBlock on cs__testimonial_with_image {
    testimonials {
      image {
        ...Asset
      }
      quote
      author_name
      author_job_description
    }
    previous_button_screen_reader_text
    next_button_screen_reader_text
  }
`;

const TestimonialWithImageBlock: React.FC<TestimonialWithImageBlockProps> = ({
  testimonial_with_image,
}) => {
  const testimonials = useMemo(
    () =>
      testimonial_with_image.testimonials
        .map((testimonial, index) => {
          const image = isPreview
            ? processImageAsset(testimonial.image)
            : processMandatoryImage(testimonial.image);
          if (!image) {
            nonFatalBuildError(
              'Image for Testimonial with Image missing!',
              `Testimonial with Image ${index + 1} needs an image in order to be displayed.`
            );
            return;
          }
          return {
            image: processMandatoryImage(testimonial.image),
            quote: testimonial.quote,
            authorName: testimonial.author_name ?? undefined,
            authorJobDescription: testimonial.author_job_description ?? undefined,
          };
        })
        .filter((testimonial) => !!testimonial),
    [testimonial_with_image]
  );
  return (
    <StyledTestimonialWithImage
      data-cy="TestimonialWithImage"
      testimonials={testimonials}
      useStickArrows
      previousButtonScreenReaderText={
        testimonial_with_image.previousButtonScreenReaderText
      }
      nextButtonScreenReaderText={testimonial_with_image.nextButtonScreenReaderText}
    />
  );
};

export default React.memo(TestimonialWithImageBlock);
